import {
  Alert,
  CondensedButton,
  Flex,
  Heading,
  IconDownloadLine,
  Spinner,
  Text,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useState } from "react";
import { TaskService, TaskTitle } from "../../../services/TaskService";
import { waitForExpectedResult } from "../../../utils/Timer";
import { Card } from "../../Card";
import {
  ERROR,
  useNotification,
  WARNING,
} from "../../CustomHooks/NotificationProvider";

const JAEvidence = ({ assessmentPlan, collectingEvidence }) => {
  return (
    <View>
      <Heading as="h3" level="h4" margin="0 0 small 0">
        {t("evidence.juriedAssessmentEvidence")}
      </Heading>
      {collectingEvidence === 0 ? (
        <Alert variant="info">{t("dashboard.noEvidence")}</Alert>
      ) : (
        <Flex justifyItems="space-between" alignItems="start" wrap="wrap">
          {assessmentPlan.sections
            .flatMap((section) => section.assessments)
            .filter((assessment) => assessment.collecting_evidence)
            .map((assessment) => {
              return (
                <Flex.Item width="50%" padding="small" key={assessment.id}>
                  <AssessmentCard assessment={assessment} />
                </Flex.Item>
              );
            })}
        </Flex>
      )}
    </View>
  );
};

const AssessmentCard = ({ assessment }) => {
  const showNotification = useNotification();
  const [inProgress, setInProgress] = useState(false);

  const getField = (fieldName, fieldValue) => {
    return (
      <>
        <Text size="small" weight="bold">
          {fieldName}
        </Text>{" "}
        <Text size="small">{fieldValue}</Text>
      </>
    );
  };

  const getSubjects = (offerings) => {
    const subjects = new Set();
    offerings.forEach((offering) => {
      subjects.add(offering.course_summary.academic_subject.name);
    });
    return [...subjects].join(", ");
  };

  const getCourseLevels = (offerings) => {
    const levels = new Set();
    offerings.forEach((offering) => {
      if (
        offering.course_summary.level.course_level &&
        offering.course_summary.level.range
      ) {
        const title = `Level ${offering.course_summary.level.course_level} (${offering.course_summary.level.range})`;
        levels.add(title);
      }
    });
    return [...levels].join(", ");
  };

  const getTaskByIdCall = (taskId) => {
    return TaskService.getTaskById(taskId);
  };

  const downloadEvidence = (url) => {
    const link = document.createElement("a");
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const processCollectEvidenceTask = (task) => {
    if (task.status === "error") {
      showNotification(
        t("notifications.evidence.collectEvidencesError"),
        ERROR
      );
    } else if (!task.result || task.result.indexOf("http") !== 0) {
      showNotification(t("notifications.evidence.noEvidenceMessage"), WARNING);
    } else {
      downloadEvidence(task.result);
    }
    setInProgress(false);
  };

  const collectEvidences = () => {
    setInProgress(true);
    showNotification(t("notifications.evidence.collectEvidencesInProgress"));
    const task = {
      title: TaskTitle.collectEvidence,
      obj: { assessmentId: assessment.id },
    };
    if (assessment.assessment_type === "external") {
      task["obj"]["external"] = true;
    }
    TaskService.createTask(task)
      .then((task) => {
        waitForExpectedResult(
          () => getTaskByIdCall(task.ddl_id),
          (task) => {
            return task.status === "done" || task.status === "error";
          },
          processCollectEvidenceTask,
          () => {
            showNotification(
              t("notifications.evidence.collectEvidencesError"),
              ERROR
            );
            setInProgress(false);
          },
          60,
          3000
        );
      })
      .catch((err) => {
        console.error(err);
        showNotification(
          t("notifications.evidence.collectEvidencesError"),
          ERROR
        );
        setInProgress(false);
      });
  };

  return (
    <Card style={{ minHeight: "150px" }}>
      <Flex justifyItems="space-between" alignItems="start" wrap="wrap">
        <Flex.Item size="100%">
          <Flex justifyItems="space-between" alignItems="center" wrap="wrap">
            <Flex.Item>
              <Heading as="h4" level="h4" margin="0 0 small 0">
                {t("evidence.evidenceCardName", {
                  loNames: assessment.los.map((lo) => lo.name).join(", "),
                })}
              </Heading>
            </Flex.Item>
            <Flex.Item>
              <Text as="span" size="small">
                {t("evidence.downloadEvidence")}
              </Text>
              <CondensedButton
                renderIcon={
                  inProgress ? (
                    <Spinner
                      renderTitle={t("global.pleaseWait")}
                      size="x-small"
                    />
                  ) : (
                    IconDownloadLine
                  )
                }
                disabled={inProgress}
                onClick={collectEvidences}
              />
            </Flex.Item>
          </Flex>
        </Flex.Item>
        <Flex.Item size="50%">
          {getField(t("evidence.subjects"), getSubjects(assessment.offerings))}
        </Flex.Item>
        {getCourseLevels(assessment.offerings) && (
          <Flex.Item size="50%" textAlign="end">
            {getField(
              t("evidence.levels"),
              getCourseLevels(assessment.offerings)
            )}
          </Flex.Item>
        )}
        <Flex.Item size="100%">
          {getField(t("evidence.goalTitle"), assessment.goal.description)}
        </Flex.Item>
        <Flex.Item size="100%">
          {getField(
            t("evidence.objectiveTitle"),
            assessment.objective.description
          )}
        </Flex.Item>
      </Flex>
    </Card>
  );
};

export default JAEvidence;
